/**
 * Dashboard Service — Analysis Mode
 * 
 * Service for fetching capacity analysis data for as-is vs to-be comparison.
 * Connects to:
 *  - DACAMS projects API for as-is baseline
 *  - FTS simulations API for to-be scenario results
 *  - RunCap API for runway capacity
 *  - AirCap API for terminal capacity
 */

import api from './api';
import { simulationsService, Simulation } from './simulations';

// ===========================================
// TYPES
// ===========================================

export type DemandHorizon = 'H+24' | 'H+48' | 'H+72' | '1W' | '1M' | 'SEASON';

export interface HourlyCapacityData {
    hour: number;
    rac: number;
    demand: number;
    cmp_infra: number;
}

export interface CapacityKPIs {
    rac: number;            // Real Apron Capacity (mov/h)
    nss: number;            // Node Staffing Score
    bci: number;            // Bottleneck Capacity Index
    nop: number;            // Number of Positions
    cc: number;             // Coordination Capacity (mov/h)
    svs: number;            // Service Volume Score (%)
    cmp_infra: number;      // Infrastructure Component
    avg_taxi_time: number;  // Average taxi-out time (min)
    pax_throughput: number; // AirCap PAX throughput
    runway_movements: number; // RunCap movements/h
    otp: number;            // On-time performance (%)
}

export interface ScenarioData {
    scenario_name: string;
    scenario_id: string;
    airport_code: string;
    date_label: string;
    kpis: CapacityKPIs;
    hourly_data: HourlyCapacityData[];
    enabled_features: string[];
}

export interface DashboardComparison {
    as_is: ScenarioData;
    to_be: ScenarioData | null;
    horizon: DemandHorizon;
    deltas: Partial<Record<keyof CapacityKPIs, number>>;
    available_scenarios: { id: string; name: string }[];
}

export interface ProjectSummary {
    id: number;
    name: string;
    airport_icao: string;
}

// ===========================================
// KPI EXTRACTION HELPERS
// ===========================================

/**
 * Extract CapacityKPIs from FTS scenario_calculator output.
 * The results_kpis has structure: { scenarios: { s1_asis: {...}, s2_ams: {...}, ... } }
 */
function extractKPIsFromScenario(
    scenarioData: Record<string, any>
): CapacityKPIs {
    return {
        rac: scenarioData.rac ?? 0,
        cc: scenarioData.cc ?? 0,
        bci: scenarioData.bci ?? 0,
        nss: scenarioData.nss ?? 0,
        nop: scenarioData.nop ?? 0,
        svs: scenarioData.svs ?? 0,
        cmp_infra: scenarioData.cmp_infra ?? 0,
        avg_taxi_time: scenarioData.axot_mean ?? scenarioData.avg_taxi_time ?? 0,
        pax_throughput: scenarioData.pax_throughput ?? 0,
        runway_movements: scenarioData.runway_movements ?? 0,
        otp: scenarioData.otp ?? 0,
    };
}

/**
 * Generate synthetic hourly data from KPIs (until backend provides real hourly breakdown).
 * Uses peak-hour shaping to create a realistic 24h profile.
 */
function generateHourlyFromKPIs(kpis: CapacityKPIs): HourlyCapacityData[] {
    return Array.from({ length: 24 }, (_, h) => {
        const peakFactor = h >= 6 && h <= 22 ? 1.0 : 0.3;
        const morningPeak = h >= 7 && h <= 10 ? 1.35 : 1.0;
        const eveningPeak = h >= 16 && h <= 20 ? 1.25 : 1.0;
        const noise = 0.95 + Math.random() * 0.1;  // ±5% random
        const demand = Math.round(kpis.cc * 0.85 * peakFactor * morningPeak * eveningPeak * noise);
        const rac = Math.round(kpis.rac * peakFactor * (0.95 + Math.random() * 0.1));
        return { hour: h, rac, demand, cmp_infra: Math.round(rac * 0.9) };
    });
}

/**
 * Generate fallback KPIs when no project/simulation data is available.
 */
function generateFallbackKPIs(isToBe: boolean = false): CapacityKPIs {
    const boost = isToBe ? 1.08 : 1.0;
    return {
        rac: Math.round(48 * boost),
        nss: Math.round(42 * boost),
        bci: Math.round(38 * boost),
        nop: Math.round(22 * boost),
        cc: Math.round(55 * boost),
        svs: Math.round(78 * boost),
        cmp_infra: Math.round(44 * boost),
        avg_taxi_time: +(12.3 / boost).toFixed(1),
        pax_throughput: Math.round(4200 * boost),
        runway_movements: Math.round(52 * boost),
        otp: +(78 * boost).toFixed(1),
    };
}

// ===========================================
// SERVICE
// ===========================================

class DashboardService {

    /**
     * Get available projects for the dashboard selector
     */
    async getProjects(): Promise<ProjectSummary[]> {
        try {
            const response = await api.get('/projects/');
            return (response.data || []).map((p: any) => ({
                id: p.id,
                name: p.name || p.project_name,
                airport_icao: p.icao_code || p.airport_icao || p.airport_data?.icao_code || 'XXXX',
            }));
        } catch {
            return [];
        }
    }

    /**
     * Get completed simulations for a project (available to-be scenarios)
     */
    async getCompletedSimulations(projectId: string): Promise<Simulation[]> {
        try {
            const sims = await simulationsService.getByProject(projectId);
            return sims.filter(s => s.status === 'COMPLETED');
        } catch {
            return [];
        }
    }

    /**
     * Build the as-is scenario from a DACAMS project's analysis results.
     */
    async getAsIsScenario(
        projectId?: number,
        airport: string = 'LEMD'
    ): Promise<ScenarioData | null> {
        let kpis: CapacityKPIs;
        let airportCode = airport;
        let projectName = 'Current';

        if (projectId) {
            try {
                const response = await api.get(`/projects/${projectId}`);
                const project = response.data;
                airportCode = project.airport_icao || project.airport_data?.icao_code || airport;
                projectName = project.name || project.project_name || 'Project';

                // Extract KPIs from analysis_results if available
                const results = project.analysis_results;
                if (results) {
                    kpis = {
                        rac: results.rac_capacity ?? results.rac ?? 0,
                        cc: results.controlling_capacity ?? results.cc ?? 0,
                        bci: results.bci_index ?? results.bci ?? 0,
                        nss: results.nss_saturation ?? results.nss ?? 0,
                        nop: results.nop_current ?? results.nop ?? 0,
                        svs: results.svs ?? 0,
                        cmp_infra: results.cmp_infra ?? 0,
                        avg_taxi_time: results.blue_time?.median_axot ?? results.axot_mean ?? 0,
                        pax_throughput: results.pax_throughput ?? 0,
                        runway_movements: results.runway_movements ?? 0,
                        otp: results.otp ?? 78,
                    };
                } else {
                    // No analysis results — return null to signal empty state
                    return null;
                }
            } catch {
                return null;
            }
        } else {
            return null;
        }

        return {
            scenario_name: `${projectName} (As-Is)`,
            scenario_id: 's1_asis',
            airport_code: airportCode,
            date_label: new Date().toISOString().slice(0, 10),
            kpis,
            hourly_data: generateHourlyFromKPIs(kpis),
            enabled_features: ['Baseline'],
        };
    }

    /**
     * Build the to-be scenario from a completed FTS simulation's results.
     */
    async getToBeScenario(
        simulationId: string,
        scenarioKey: string = 's2_ams',
        airport: string = 'LEMD'
    ): Promise<ScenarioData | null> {
        try {
            const sim = await simulationsService.getOne(simulationId);
            if (!sim || !sim.results_kpis) return null;

            const scenariosData = sim.results_kpis?.scenarios || sim.results_kpis;
            const scenarioData = scenariosData[scenarioKey] || scenariosData['s2_ams'] || Object.values(scenariosData)[0];

            if (!scenarioData) return null;

            const kpis = extractKPIsFromScenario(scenarioData);
            const enabledFeatures: string[] = [];
            if (sim.ams_enabled) enabledFeatures.push('AMS');
            if (sim.fastgate_enabled) enabledFeatures.push('FASTGate');
            if (sim.turnaround_monitoring_enabled) enabledFeatures.push('Turnaround Monitoring');
            if (sim.infra_changes_enabled) enabledFeatures.push('Infrastructure Changes');
            if (sim.new_sop_enabled) enabledFeatures.push('New SOP');
            if (sim.dacams_improvements_enabled) enabledFeatures.push('DACAMS Improvements');

            return {
                scenario_name: `${sim.name} (To-Be)`,
                scenario_id: scenarioKey,
                airport_code: airport,
                date_label: sim.updated_at?.slice(0, 10) || new Date().toISOString().slice(0, 10),
                kpis,
                hourly_data: generateHourlyFromKPIs(kpis),
                enabled_features: enabledFeatures,
            };
        } catch {
            return null;
        }
    }

    /**
     * Build the full comparison page data.
     * 
     * If projectId/simulationId are provided, fetch real data;
     * otherwise use fallback demo data.
     */
    async getComparison(
        horizon: DemandHorizon = 'H+24',
        projectId?: number,
        simulationId?: string,
        scenarioKey: string = 's2_ams',
        airport: string = 'LEMD'
    ): Promise<DashboardComparison | null> {
        // 1. Fetch as-is (returns null if no analysis exists)
        const asIs = await this.getAsIsScenario(projectId, airport);
        if (!asIs) return null;

        // 2. Fetch to-be (only if simulation selected)
        let toBe: ScenarioData | null = null;
        if (simulationId) {
            toBe = await this.getToBeScenario(simulationId, scenarioKey, airport);
        }
        // No fallback — if no simulation, to-be stays null

        // 3. Calculate deltas
        const deltas: Partial<Record<keyof CapacityKPIs, number>> = {};
        if (toBe) {
            for (const key of Object.keys(asIs.kpis) as (keyof CapacityKPIs)[]) {
                deltas[key] = +(toBe.kpis[key] - asIs.kpis[key]).toFixed(2);
            }
        }

        // 4. Available scenarios for the selector
        const available_scenarios = [
            { id: 's1_asis', name: 'Baseline (As-Is)' },
            { id: 's2_ams', name: 'AMS Optimized' },
            { id: 's3_fastgate', name: 'FASTGate' },
            { id: 's4_infrastructure', name: 'Infrastructure Changes' },
        ];

        return { as_is: asIs, to_be: toBe, horizon, deltas, available_scenarios };
    }
}

export const dashboardService = new DashboardService();
export default dashboardService;
